import { DateTime } from "luxon";
import { AUDIT_STATUS_LABELS } from "../../const/audit";
import { DEFAULT_FALLBACK_LOCALE } from "../../const/locale";
import { getReportName } from "./report";
import { isValid, validateUrlRegex } from "../common/validators";

export const DEFAULT_DATE_FORMAT = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
};

export const DEFAULT_TIME_FORMAT = {
  hour12: false,
  hour: "2-digit",
  minute: "2-digit",
};

export const DEFAULT_DATE_TIME_FORMAT = {
  ...DEFAULT_DATE_FORMAT,
  ...DEFAULT_TIME_FORMAT,
};

function parseDateInput(timestamp) {
  if (timestamp == null) {
    return null;
  }

  const normalizedTimestamp =
    typeof timestamp === "string" && /^-?\d+$/.test(timestamp.trim())
      ? Number(timestamp)
      : timestamp;
  const date =
    normalizedTimestamp instanceof Date ? normalizedTimestamp : new Date(normalizedTimestamp);

  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Parse a date to Date String format
 * @param {number} timestamp the timestamp property of the object
 * @param {string} [locale=undefined] Locale which specifies the language whose formatting conventions should be used. Defaults to undefined, in which case the machine's time will be used
 * @param {object} [dateFormat=DEFAULT_DATE_FORMAT] Defines custom formatting options for the output, which should comform to Intl.DateTimeFormatOptions
 * @param {string} [logger=undefined] Optional logger for backend to log errors
 * @returns {string} A string representing the date portion of the given Date instance according to language-specific conventions
 */
export const formatTimestamp = (
  timestamp,
  locale = undefined,
  dateFormat = DEFAULT_DATE_FORMAT
) => {
  const date = parseDateInput(timestamp);

  if (date == null) {
    console.warn("formatTimestamp received invalid date input", timestamp);
    return "";
  }
  return date.toLocaleDateString(locale, dateFormat);
};

/**
 * Parse a date to Date String format with time
 * @param {number} timestamp the timestamp property of the object
 * @param {string} [locale=undefined] Locale which specifies the language whose formatting conventions should be used. Defaults to undefined, in which case the machine's time will be used
 * @param {object} [dateFormat=DEFAULT_DATE_TIME_FORMAT] Defines custom formatting options for the output, which should comform to Intl.DateTimeFormatOptions
 * @param {string} [logger=undefined] Optional logger for backend to log errors
 * @returns {string} A string representing the date portion of the given Date instance according to language-specific conventions
 */
export const formatTimestampWithTime = (
  timestamp,
  locale = undefined,
  dateTimeFormat = DEFAULT_DATE_TIME_FORMAT
) => {
  return formatTimestamp(timestamp, locale, dateTimeFormat);
};

/**
 * Parse a date to [... ago] format
 * @param {number} date the timestamp property of the object
 * @param {"long" | "short" | "narrow"} style style of the formatted time
 * @param {string} [locale=DEFAULT_FALLBACK_LOCALE] Locale which specifies the language whose formatting conventions should be used
 * @returns {string} the formatted time
 */
export const formatTimeAgo = (date, style = "long", locale = DEFAULT_FALLBACK_LOCALE) => {
  const units = ["year", "month", "week", "day", "hour", "minute", "second"];
  if (date == null) {
    console.error("formatTimeAgo received invalid date input", date);
    return "";
  }
  let dateTime = DateTime.fromMillis(date);
  const diff = dateTime.diffNow().shiftTo(...units);
  const unit = units.find((unit) => diff.get(unit) !== 0) || "second";
  const relativeFormatter = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
    style: style,
  });
  return relativeFormatter.format(Math.trunc(diff.as(unit)), unit);
};

/**
 * Format a date to [... ago] if it is within one day, otherwise use formatTimestamp() function
 * @param {number} time the timestamp property of the object
 * @param {"long" | "short" | "narrow"} [style="long"] style of the formatted time
 * @param {string} [locale=DEFAULT_FALLBACK_LOCALE] Locale which specifies the language whose formatting conventions should be used
 * @param {string} [numeric=DEFAULT_DATE_FORMAT] Defines whether the date should be written out fully in numeric (with leading zeros), e.g.:09/07/2022
 * @returns {string} the formatted time
 */
export const formatTimeAgoIfWithinOneDay = (
  time,
  style = "long",
  locale = DEFAULT_FALLBACK_LOCALE,
  dateFormat = DEFAULT_DATE_FORMAT
) => {
  if (time == null || isNaN(time)) {
    console.warn("formatTimeAgoIfWithinOneDay received invalid date input", time);
    return "";
  }
  const now = Date.now();
  const delta = now - time;
  const day = 8.64e7;
  if (delta < 1 * day) {
    return formatTimeAgo(time, style, locale);
  } else {
    return formatTimestamp(time, locale, dateFormat);
  }
};

export const formatAuditFieldsForEmailRendering = (property, value, projectInfo = null) => {
  if (property == null || value == null) {
    // value can be 0
    return null;
  }

  switch (property) {
    case "expectedDeadline":
      return formatTimestamp(value);
    case "categories":
      if (!Array.isArray(value)) {
        return customReplaceAll(JSON.stringify(value), '"', "");
      } else {
        return value.join(" & ");
      }
    case "auditReportList":
      if (!Array.isArray(value)) {
        return null;
      } else if (value.length === 0) {
        return null;
      } else {
        return value
          .sort((a, b) => a.createdAt - b.createdAt)
          .map((item) => {
            let fullReportName = getReportName(projectInfo, item);
            if (item.fileUrl != null && isValid(validateUrlRegex(item.fileUrl))) {
              fullReportName += " (PDF)";
            }
            if (item.showSignOff === true) {
              fullReportName += " (Signable)";
            }
            return fullReportName;
          })
          .join(", "); // Return string of comma-separated file names
      }
    case "assignedCertikDevList":
    case "attachments":
    case "images":
      if (!Array.isArray(value)) {
        return null;
      } else {
        return value.join(", ");
      }
    // TODO: Implement server-side version of richTxtToPlainTxt() function for this to work
    // case "scopeOfWorkGithub":
    // case "scopeOfWorkAdditional": // obsolete field, keep it for compatibility
    // case "scopeOfWorkOnChain":
    //   return richTxtToPlainTxt(value).split("\n");
    case "findings":
      return customReplaceAll(JSON.stringify(value, undefined, 2), '"', "").split("\n");
    case "status":
      return AUDIT_STATUS_LABELS[value].label;
    case "blockPublishingAction":
      return value.blocked ? "Blocked" : "Unblocked";
    default:
      return customReplaceAll(JSON.stringify(value), '"', "");
  }
};

/**
 * Replaces all instances of `targetString` with `replaceString` in 'srcString'.
 * This function intends to temporarily replicate node's `replaceAll` function since node's `replaceAll`
 * is currently not supported in node versions < 15, and our vercel environment is at v14.
 *
 * @param {string} srcString the string to perform replacement on
 * @param {string} targetString the string we want to match
 * @param {string} replaceString the string we want to replaced the matched string (target) with
 * @returns {string} returns the string with replacement done on
 */
export function customReplaceAll(srcString, targetString, replaceString) {
  if (targetString == null || srcString == null || replaceString == null) {
    throw new TypeError("customReplaceAll input strings cannot be null or undefined.");
  }

  replaceString = replaceString.replace(/\$/g, "$$$$");
  return srcString.replace(
    // eslint-disable-next-line no-useless-escape
    new RegExp(targetString.replace(/([\/\,\!\\\^\$\{\}\[\]\(\)\.\*\+\?\|<>\-\&])/g, "\\$&"), "g"),
    replaceString
  );
}
